// GameSession: the UI's only doorway to the engine. Holds authoritative GameState,
// a queue of pending commands (the player's orders for this month), and the
// event-sourced save (seed + command log — replay reproduces the state exactly,
// which is the engine's golden-master guarantee doing double duty as save/load).
// Pending orders ride along in the save so a mid-month refresh loses nothing.
//
// The UI reads ONLY playerView(state) — hidden bug state never crosses this line.

import type { Command, ContentPack, EngineEvent, GameState, PlayerView, TickReport } from "../engine/types.js";
import { initState, step } from "../engine/step.js";
import { playerView } from "../engine/view.js";

const SAVE_KEY = "first-silicon:save:v1";

/** Saves that predate scenario select (P1) carry no scenarioId — they are all this. */
export const LEGACY_SCENARIO_ID = "founders-1993";

export interface SaveFile {
  contentVersion: string;
  /** Which scenario's content pack replays this log (v2; absent = legacy scenario). */
  scenarioId?: string;
  seed: number;
  log: Command[][];
  pending?: Command[]; // orders queued this month but not yet executed (absent in pre-fix saves)
}

export interface SessionSnapshot {
  view: PlayerView;
  report: TickReport | null;
  pending: readonly Command[];
  feed: readonly EngineEvent[];
  seed: number;
  turn: number;
}

type Listener = () => void;

/** Commands where a newer order replaces an older one instead of stacking. */
function replaceKey(c: Command): string | null {
  switch (c.type) {
    case "setNre":
      return "setNre";
    case "raiseRound":
      return "raiseRound";
    case "setStaffing":
      return `setStaffing:${c.projectId}`;
    case "setYieldResearch":
      return `setYieldResearch:${c.nodeId}`;
    case "setReliability":
      return `setReliability:${c.nodeId}`;
    case "startNodeQual":
      return `startNodeQual:${c.nodeId}`;
    case "endOfLifeProduct":
      return `eol:${c.productId}`;
    case "tapeIn":
    case "tapeOut":
    case "respin":
    case "cancelProject":
      return `decision:${c.projectId}`; // one pending decision per project
    case "hire":
    case "fire":
      return `${c.type}:${c.discipline}`;
    case "acceptTermSheet":
    case "rejectTermSheet":
      return "termSheet"; // one answer to the sheet on the table
    case "fileS1":
      return "fileS1";
    case "priceIpo":
      return "priceIpo";
    case "setGuidance":
      return "setGuidance";
    case "secondaryOffering":
      return "secondary";
    case "buyback":
      return "buyback";
    case "setChannelPolicy":
      return `channel:${c.marketId}`;
    case "answerAsk":
      return `ask:${c.askId}`; // one answer per decision
    case "setCompPolicy":
      return "compPolicy";
    case "setEdaTier":
      return "edaTier";
    case "setToolStaff":
      return `toolStaff:${c.discipline}`;
    case "acquireRival":
      return `acquire:${c.rivalId}`;
    case "setGarageAlloc":
      return "garageAlloc";
    case "schedulePitch":
      return "schedulePitch";
    case "foldCompany":
      return "foldCompany";
    case "setTreeStaff":
      return `treeStaff:${c.nodeId}`;
    default:
      return null; // startProject stacks
  }
}

export class GameSession {
  readonly content: ContentPack;
  private state: GameState;
  private lastReport: TickReport | null = null;
  private pending: Command[] = [];
  private log: Command[][] = [];
  private feed: EngineEvent[] = [];
  private listeners = new Set<Listener>();
  private snapshot: SessionSnapshot | null = null;
  readonly seed: number;

  constructor(content: ContentPack, seed: number, log?: Command[][], pending?: Command[]) {
    this.content = content;
    this.seed = seed;
    this.state = initState(content, seed);
    if (log) {
      for (const commands of log) {
        if (this.state.dead) break;
        const r = step(this.state, commands, content);
        this.state = r.state;
        this.lastReport = r.report;
        this.log.push(commands);
        this.pushEvents(r.report.events);
      }
    }
    this.pending = pending ? [...pending] : [];
    // save immediately: a fresh campaign (turn 0, new-game seed) must survive a refresh
    this.save();
  }

  // ---- React store contract ----
  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): SessionSnapshot => {
    if (!this.snapshot) {
      this.snapshot = {
        view: playerView(this.state),
        report: this.lastReport,
        pending: [...this.pending],
        feed: [...this.feed],
        seed: this.seed,
        turn: this.log.length,
      };
    }
    return this.snapshot;
  };

  private invalidate(): void {
    this.snapshot = null;
    for (const fn of this.listeners) fn();
  }

  private pushEvents(events: EngineEvent[]): void {
    this.feed.push(...events);
    if (this.feed.length > 250) this.feed.splice(0, this.feed.length - 250);
  }

  // ---- orders ----
  queue(command: Command): void {
    const key = replaceKey(command);
    if (key !== null) {
      this.pending = this.pending.filter((c) => replaceKey(c) !== key);
    }
    this.pending.push(command);
    this.save();
    this.invalidate();
  }

  unqueue(index: number): void {
    this.pending.splice(index, 1);
    this.save();
    this.invalidate();
  }

  clearPending(): void {
    this.pending = [];
    this.save();
    this.invalidate();
  }

  /** The value a numeric order would set, if one is pending (for optimistic inputs). */
  pendingValue<T extends Command["type"]>(type: T, match?: Partial<Command>): Command | undefined {
    return this.pending.find(
      (c) =>
        c.type === type &&
        Object.entries(match ?? {}).every(([k, v]) => (c as unknown as Record<string, unknown>)[k] === v),
    );
  }

  // ---- the turn loop ----
  endTurn(): TickReport | null {
    if (this.state.dead) return null;
    const commands = this.pending;
    this.pending = [];
    const r = step(this.state, commands, this.content);
    this.state = r.state;
    this.lastReport = r.report;
    this.log.push(commands);
    this.pushEvents(r.report.events);
    this.save();
    this.invalidate();
    return r.report;
  }

  // ---- persistence (event-sourced: seed + command log) ----
  save(): void {
    try {
      localStorage.setItem(SAVE_KEY, this.exportSave());
    } catch {
      // storage full/unavailable: play on without autosave
    }
  }

  exportSave(): string {
    const file: SaveFile = {
      contentVersion: this.content.version,
      scenarioId: this.content.scenario.id,
      seed: this.seed,
      log: this.log,
      pending: this.pending,
    };
    return JSON.stringify(file);
  }

  static load(content: ContentPack): GameSession | null {
    return GameSession.fromRaw(content, safeGet(SAVE_KEY));
  }

  /** Which scenario the autosave belongs to (so the boot path can build the
   *  right content pack before replaying), or null when there is no autosave. */
  static savedScenarioId(): string | null {
    return GameSession.peekScenarioId(safeGet(SAVE_KEY));
  }

  /** Scenario id of a raw SaveFile string; null when it isn't one. */
  static peekScenarioId(raw: string | null): string | null {
    if (!raw) return null;
    try {
      const file = JSON.parse(raw) as SaveFile;
      if (!Number.isInteger(file.seed) || !Array.isArray(file.log)) return null;
      return file.scenarioId ?? LEGACY_SCENARIO_ID;
    } catch {
      return null;
    }
  }

  static clearSave(): void {
    try {
      localStorage.removeItem(SAVE_KEY);
    } catch {
      // ignore
    }
  }

  // ---- save slots (M2, design §16 "save/load") ----
  // Slots hold the same event-sourced SaveFile as the autosave; loading a slot
  // constructs a fresh session (which immediately autosaves over SAVE_KEY).

  static readonly SLOT_COUNT = 3;

  private static slotKey(slot: number): string {
    return `${SAVE_KEY}:slot${slot}`;
  }

  saveToSlot(slot: number): void {
    try {
      localStorage.setItem(GameSession.slotKey(slot), this.exportSave());
    } catch {
      // storage full/unavailable
    }
    this.invalidate(); // slot list is derived state — let the UI re-read it
  }

  static loadSlot(content: ContentPack, slot: number): GameSession | null {
    return GameSession.fromRaw(content, safeGet(GameSession.slotKey(slot)));
  }

  static clearSlot(slot: number): void {
    try {
      localStorage.removeItem(GameSession.slotKey(slot));
    } catch {
      // ignore
    }
  }

  static slotInfo(
    content: ContentPack,
    slot: number,
  ): { seed: number; turn: number; scenarioId: string; compatible: boolean } | null {
    const raw = safeGet(GameSession.slotKey(slot));
    if (!raw) return null;
    try {
      const file = JSON.parse(raw) as SaveFile;
      if (!Number.isInteger(file.seed) || !Array.isArray(file.log)) return null;
      return {
        seed: file.seed,
        turn: file.log.length,
        scenarioId: file.scenarioId ?? LEGACY_SCENARIO_ID,
        compatible: file.contentVersion === content.version,
      };
    } catch {
      return null;
    }
  }

  static slotRaw(slot: number): string | null {
    return safeGet(GameSession.slotKey(slot));
  }

  /** Parse an exported save (file import). Returns a session or a reason it can't load.
   *  Callers peek the scenarioId first and pass the matching content pack. */
  static import(content: ContentPack, text: string): GameSession | string {
    try {
      const file = JSON.parse(text) as SaveFile;
      if (!Number.isInteger(file.seed) || !Array.isArray(file.log)) return "not a FIRST SILICON save file";
      if (file.contentVersion !== content.version) {
        return `save is for content ${file.contentVersion}; this build runs ${content.version}`;
      }
      if ((file.scenarioId ?? LEGACY_SCENARIO_ID) !== content.scenario.id) {
        return `save is for scenario ${file.scenarioId ?? LEGACY_SCENARIO_ID}; loaded content is ${content.scenario.id}`;
      }
      return new GameSession(content, file.seed, file.log, Array.isArray(file.pending) ? file.pending : undefined);
    } catch {
      return "could not parse the file as JSON";
    }
  }

  private static fromRaw(content: ContentPack, raw: string | null): GameSession | null {
    if (!raw) return null;
    try {
      const file = JSON.parse(raw) as SaveFile;
      if (file.contentVersion !== content.version || !Number.isInteger(file.seed)) return null;
      if ((file.scenarioId ?? LEGACY_SCENARIO_ID) !== content.scenario.id) return null;
      return new GameSession(content, file.seed, file.log, Array.isArray(file.pending) ? file.pending : undefined);
    } catch {
      return null;
    }
  }
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
